/**
 * Route copy for `src/routes/landing` (keys: `routes.landing.<file>.<key>`).
 *
 * `fr` is the source; `en` and `ar` are typed against it, so a missing or extra
 * key fails `tsc` (and parity.test.ts). Keys are referenced from the route files
 * through `t()` / `tr()` — edit values freely, never rename a key without its call site.
 *
 * Locale file: en (typed against ./fr). One file per locale so
 * every non-default locale is a separate, lazily-loaded chunk.
 */

import type { Catalog } from './fr';

const en: Catalog = {
  landingData: {
    allInOne: 'All-in-one',
    theAllInOneAiPowered: 'The all-in-one, AI-powered platform',
    features: 'Features',
    everythingKarexiaDoes: 'Everything Karexia does',
    preview: 'Preview',
    pricing: 'Pricing',
    whoItSFor: 'Who it\'s for',
    socialMediaContentCrmProspecting: 'Social media · Content · CRM · Prospecting',
    yourEntireOnlinePresenceRunBy: 'Your entire online presence, run by your AI team.',
    karexiaBringsAiContentCreationMulti: 'Karexia brings AI content creation, multi-network scheduling and publishing, a full CRM and B2B prospecting together in one platform. Hand repetitive work to AI experts available around the clock.',
    signIn: 'Sign in',
    createAnAccount: 'Create an account',
    compliantWithQuebecLaw25And: 'Compliant with Quebec Law 25 and CASL — your sends are protected by an anti-spam safeguard.',
    bilingualAiContent: 'Bilingual AI content',
    multiNetworkPublishing: 'Multi-network publishing',
    crmProspecting: 'CRM + prospecting',
    n247AiCopilot: '24/7 AI Copilot',
    aiContent: 'AI content',
    generatePostsVisualsInfographicsAndVideo: 'Generate posts, visuals, infographics and videos in seconds. Brand tone, per-network variants and bilingual versions, ready to publish.',
    schedulingPublishing: 'Scheduling & publishing',
    scheduleAndPublishToAllYour: 'Schedule and publish to all your networks from a single calendar: LinkedIn, Facebook, Instagram, X, YouTube, TikTok. Smart queue and automatic recycling.',
    crmB2bProspecting: 'CRM & B2B prospecting',
    aFullCrmContactsCompaniesPipeline: 'A full CRM — contacts, companies, pipeline, follow-ups — paired with multi-source B2B prospecting to build and enrich your lists.',
    aiCopilot247ExpertTeam: 'AI Copilot + 24/7 expert team',
    aCopilotThatCarriesOutYour: 'A copilot that carries out your actions in the platform, backed by a team of specialized AI agents working for you day and night.',
    analyticsAccountIntelligence: 'Analytics & account intelligence',
    trackPerformanceReturnOnInvestmentAnd: 'Track performance, return on investment and audience. Per-account intelligence briefs turn your data into next actions.',
    outreachCommunity: 'Outreach & community',
    multichannelSequencesAUnifiedInboxCommun: 'Multichannel sequences, a unified inbox, community management and moderation — all governed by a compliant anti-spam safeguard.',
    aiContentStudio: 'AI content studio',
    aiGeneratedCopyVisualsCarouselsAnd: 'AI-generated copy, visuals, carousels and videos.',
    creativeStudio: 'Creative studio',
    graphicEditorBrandTemplatesAndAnimated: 'Graphic editor, brand templates and animated infographics.',
    editorialCalendar: 'Editorial calendar',
    visualSchedulingAndMultiNetworkPublishin: 'Visual scheduling and multi-network publishing.',
    queueRecycling: 'Queue & recycling',
    automaticRecyclingOfEvergreenContentAnd: 'Automatic recycling of evergreen content and categories.',
    multiNetwork: 'Multi-network',
    linkedinFacebookInstagramXYoutubeTiktok: 'LinkedIn, Facebook, Instagram, X, YouTube, TikTok.',
    fullCrm: 'Full CRM',
    contactsCompaniesGroupsPipelineAndDeals: 'Contacts, companies, groups, pipeline and deals.',
    b2bProspecting: 'B2B prospecting',
    multiSourceGeotargetedSearchAndEnrichmen: 'Multi-source geotargeted search and enrichment.',
    prospectRecommendations: 'Prospect recommendations',
    prioritizedByProductFitAndGoal: 'Prioritized by product fit and goal.',
    outreachSequences: 'Outreach sequences',
    multichannelCampaignsEmailLinkedinSmsWha: 'Multichannel campaigns: email, LinkedIn, SMS, WhatsApp.',
    unifiedInboxUnibox: 'Unified inbox (Unibox)',
    allYourIncomingConversationsInOne: 'All your incoming conversations in one place.',
    communityManagement: 'Community management',
    moderationSavedRepliesAndSlaPolicies: 'Moderation, saved replies and SLA policies.',
    aiCopilot: 'AI Copilot',
    anAssistantThatActsAcrossThe: 'An assistant that acts across the whole platform for you.',
    n247AiTeam: '24/7 AI team',
    specializedAgentsThatCarryOutYour: 'Specialized agents that carry out your assignments around the clock.',
    advancedAnalytics: 'Advanced analytics',
    performanceRoiFunnelsCohortsAndAttributi: 'Performance, ROI, funnels, cohorts and attribution.',
    advertising: 'Advertising',
    adCampaignCreationAndTargeting: 'Ad campaign creation and targeting.',
    servicesMarketplace: 'Services marketplace',
    providerDirectoryBookingsAndReviews: 'Provider directory, bookings and reviews.',
    linkInBioProjects: 'Link-in-bio & projects',
    publicBioPagesProjectsAndApproval: 'Public bio pages, projects and approval flows.',
    law25CaslCompliance: 'Law 25 / CASL compliance',
    antiSpamSafeguardAndConsentEnforcement: 'Anti-spam safeguard and consent enforcement.',
    free: 'Free',
    toGetStarted: 'to get started',
    tryAiContentCreationAndPublishing: 'Try AI content creation and publishing on your first networks.',
    aiContentStudioCreditsIncluded: 'AI content studio (credits included)',
    publishingTo3Networks: 'Publishing to 3 networks',
    crmUpTo500Contacts: 'CRM — up to 500 contacts',
    custom: 'Custom',
    perMonth: 'per month',
    forTeamsThatPublishEveryDay: 'For teams that publish every day and prospect actively.',
    expandedAiCredits: 'Expanded AI credits',
    unlimitedNetworks: 'Unlimited networks',
    b2bProspectingEnrichment: 'B2B prospecting & enrichment',
    multichannelOutreachSequences: 'Multichannel outreach sequences',
    advancedAnalyticsRoi: 'Advanced analytics & ROI',
    n247AiExpertTeam: '24/7 AI expert team',
    volumeCollaborationAndGovernanceForDeman: 'Volume, collaboration and governance for demanding organizations.',
    volumeBasedAiCredits: 'Volume-based AI credits',
    collaborationTeamRoles: 'Collaboration & team roles',
    governanceAuditTrails: 'Governance & audit trails',
    developerApiIntegrations: 'Developer API & integrations',
    dedicatedSupport: 'Dedicated support',
    pricing2: 'Pricing',
    aPlanForEveryStageOf: 'A plan for every stage of your growth',
    startForFreeThenScaleAt: 'Start for free, then scale at your own pace. Every plan includes AI credits, multi-network publishing and the Copilot.',
    chooseThisPlan: 'Choose this plan',
    signInToSeeLivePricing: 'Sign in to see live pricing and the AI credits included in your region.',
    mostPopular: 'Most popular',
    n2MonthsFree: '2 months free',
    dashboard: 'Dashboard',
    yourCockpitContentPostsConnectedAccounts: 'Your cockpit: content, posts, connected accounts, AI credits and next actions.',
    generateCopyVisualsAndVideosAdapted: 'Generate copy, visuals and videos, adapted to each network, in one click.',
    publishingCalendar: 'Publishing calendar',
    scheduleAndPublishToAllYour2: 'Schedule and publish to all your networks from a single calendar.',
    crmPipeline: 'CRM & pipeline',
    trackContactsCompaniesAndDealsWith: 'Track contacts, companies and deals, with automatic follow-ups and reminders.',
    analyticsRoi: 'Analytics & ROI',
    measurePerformanceAudienceAndReturnOn: 'Measure performance, audience and return on investment.',
    justAskAndYourCopilotCarries: 'Just ask, and your copilot carries out the actions in the platform.',
    productPreview: 'Product preview',
    builtForSpeedDesignedToLast: 'Built for speed, designed to last',
    aLookAtTheWorkspacesYou: 'A look at the workspaces you\'ll use every day.',
    demo: 'Demo',
    seeKarexiaInAction: 'See Karexia in action',
    twoMinutesToSeeHowTo: 'Two minutes to see how to hand your online presence to your AI team.',
    videoDemoComingSoon: 'Video demo coming soon',
    playTheVideo: 'Play the video',
    marketingAgencies: 'Marketing agencies',
    manageMultipleClientsAndBrandsWithout: 'Manage multiple clients and brands without multiplying tools.',
    jugglingTenToolsPerClient: 'Juggling ten tools per client',
    producingContentAtScale: 'Producing content at scale',
    provingRoiToClients: 'Proving ROI to clients',
    contentAndCalendarCentralizedPerBrand: 'Content and calendar centralized per brand',
    assemblyLineAiProduction: 'Assembly-line AI production',
    clearPerformanceReports: 'Clear performance reports',
    smallMediumBusinesses: 'Small & medium businesses',
    aProfessionalPresenceEvenWithoutA: 'A professional presence, even without a dedicated marketing team.',
    lackOfTimeAndResources: 'Lack of time and resources',
    hardToStayConsistent: 'Hard to stay consistent',
    tediousProspecting: 'Tedious prospecting',
    theAiTeamPublishesAndProspects: 'The AI team publishes and prospects for you',
    consistencyGuaranteed: 'Consistency guaranteed',
    newCustomersWithoutManualEffort: 'New customers without manual effort',
    consultantsCreators: 'Consultants & creators',
    buildYourPersonalBrandAndConvert: 'Build your personal brand and convert your audience.',
    creatingContentContinuously: 'Creating content continuously',
    turningTheAudienceIntoCustomers: 'Turning the audience into customers',
    stayingVisibleWithoutSpendingAllDay: 'Staying visible without spending all day on it',
    aiGhostwriterInYourVoice: 'AI ghostwriter in your voice',
    linkInBioAndLeadCapture: 'Link-in-bio and lead capture',
    dataDrivenGrowth: 'Data-driven growth',
    firmsProfessionalServices: 'Firms & professional services',
    growYourClientBaseWithFull: 'Grow your client base with full compliance.',
    reputationAndComplianceToProtect: 'Reputation and compliance to protect',
    scatteredClientFollowUp: 'Scattered client follow-up',
    irregularBusinessDevelopment: 'Irregular business development',
    law25CaslCompliantSafeguard: 'Law 25 / CASL-compliant safeguard',
    structuredCrmAndFollowUps: 'Structured CRM and follow-ups',
    targetedMeasurableB2bProspecting: 'Targeted, measurable B2B prospecting',
    designedForTheWayYouWork: 'Designed for the way you work',
    karexiaAdaptsToTheWayYou: 'Karexia adapts to the way you work, whatever your team.',
    theChallenge: 'The challenge',
    withKarexia: 'With Karexia',
    socialNetworksSupported: 'social networks supported',
    aiExpertTeamAtWork: 'AI expert team at work',
    contentCrmProspectingAnalytics: 'content, CRM, prospecting, analytics',
    builtForCanadianCompliance: 'built for Canadian compliance',
    whyKarexia: 'Why Karexia',
    onePlatformInsteadOfTenTools: 'One platform instead of ten tools',
    replaceYourMarketingToolStackWith: 'Replace your marketing tool stack with one unified platform.',
    handRepetitiveWorkToAiAgents: 'Hand repetitive work to AI agents, not to yet another spreadsheet.',
    stayCompliantEverySendGoesThrough: 'Stay compliant: every send goes through an anti-spam safeguard.',
    readyToHandOffToYour: 'Ready to hand off to your AI team?',
    signInToCreateYourFirst: 'Sign in to create your first content, schedule your posts and let your AI experts work for you.',
    theAllInOneAiPowered2: 'The all-in-one, AI-powered platform for your online presence.',
    product: 'Product',
    capabilities: 'Capabilities',
    solutions: 'Solutions',
    agencies: 'Agencies',
    consultants: 'Consultants',
    professionalServices: 'Professional services',
    privacy: 'Privacy',
    privacyPolicyEn: 'Privacy policy (EN)',
    allRightsReserved: 'All rights reserved.',
    monthly: 'Monthly',
    annual: 'Annual',
    professional: 'Professional',
    company: 'Company',
    results: 'Results',
    linkedinFacebookInstagramXThreadsBluesky: 'LinkedIn, Facebook, Instagram, X, Threads, Bluesky, YouTube, TikTok, Pinterest and more.',
    customerResults: 'Customer results',
  },
  landingPage: {
    skipToContent: 'Skip to content',
  },
  sectionsFeatureCards3D: {
    features: 'Features',
    onePlatformSixSuperpowers: 'One platform, six superpowers',
    everythingYouNeedToCreatePublish: 'Everything you need to create, publish, prospect and measure — all in one place.',
    learnMore: 'Learn more',
  },
  sectionsFeatureGrid: {
    everythingKarexiaDoes: 'Everything Karexia does',
    aCompleteMarketingToolbox: 'A complete marketing toolbox',
    replaceADozenToolsWithA: 'Replace a dozen tools with a single, consistent, AI-powered platform.',
  },
  sectionsHero: {
    appKarexiaComDashboard: 'app.karexia.com/dashboard',
    aiTeam: 'AI team',
    atWork247: 'at work 24/7',
  },
  sectionsLandingNav: {
    mainNavigation: 'Main navigation',
    signIn: 'Sign in',
    createAnAccount: 'Create an account',
    closeMenu: 'Close menu',
    openMenu: 'Open menu',
  },
  sectionsPlansSection: {
    aiCredits: 'AI credits',
    aiCreditsMonth: 'AI credits / month',
    socialAccounts: 'Social accounts',
    connectedAccounts: 'Connected accounts',
    teamMembers: 'Team members',
    scheduledPosts: 'Scheduled posts',
    workspaces: 'Workspaces',
    storageGb: 'Storage (GB)',
    unlimited: 'unlimited',
    billingFrequency: 'Billing frequency',
    free: 'Free',
    month: '/ month',
    billedAnnuallyVYear: 'Billed annually — {{v}} / year',
    billedMonthly: 'Billed monthly',
    currencyNotConfiguredForThisPlan: 'Currency not configured for this plan — price in Canadian dollars.',
    billedInCurrency: '≈ billed in {{currency}}',
    aiCreditsPerMonth: 'AI credits per month',
    additionalCreditsAtVEach: 'Additional credits at {{v}} each',
    projects: 'Projects',
    contacts: 'Contacts',
    users: 'Users',
    campaigns: 'Campaigns',
  },
  sectionsScreenshots: {
    previous: 'Previous',
    next: 'Next',
    previewTitle: 'Preview: {{title}}',
  },
  v2: {
    "nav": {
      "demo": "Demo",
      "product": "Product",
      "reels": "Reels",
      "trust": "Trust",
      "pricing": "Pricing",
      "faq": "FAQ",
      "results": "Results"
    },
    "hero": {
      "eyebrow": "Your AI marketing co-founder",
      "title": "Paste your URL. Karexia builds the marketing plan and runs it with you.",
      "subtitle": "Autopilot, AI content, Reels, multi-network publishing, CRM and compliant prospecting: one platform, a team of AI agents, and you stay in control of every external action.",
      "ctaPrimary": "Create a free account",
      "ctaSecondary": "Watch the demos",
      "note": "Built for Quebec Law 25 and CASL: every send goes through a consent check.",
      "chip1": "Autopilot from a URL",
      "chip2": "Reels and short video",
      "chip3": "Multi-network publishing",
      "chip4": "French, English, Arabic",
      "imgAlt": "Real Karexia screenshot: the Today page with the day’s priority actions.",
      "badgeTitle": "AI team",
      "badgeText": "queues every action for your approval"
    },
    "tour": {
      "eyebrow": "Product demos",
      "title": "See Karexia at work, for real",
      "subtitle": "Five flows recorded in the production product. Short, silent, with demo data.",
      "tablist": "Choose a demo",
      "tabAutopilot": "Autopilot",
      "tabComposer": "Composer and calendar",
      "tabAiTeam": "AI team",
      "tabCrm": "CRM and email",
      "tabReel": "Reels",
      "descAutopilot": "Autopilot: paste a product address and Karexia turns it into a brief, then an action plan to approve.",
      "descComposer": "Composer: write a post, pick the networks, then schedule it in the calendar without publishing it.",
      "descAiTeam": "AI team: set an agent’s autonomy level and review an action waiting for approval.",
      "descCrm": "CRM: contact record, enrichment, then an email draft from a purpose template, nothing sent.",
      "descReel": "Video studio: the studio modes, including Reel assembly, and automatic repurposing of one idea into several formats.",
      "noAudio": "Silent demo. The description above stands in for captions.",
      "reducedMotion": "Autoplay is off because of your motion preferences: use the play button.",
      "videoLabel": "Video demo: {{title}}",
      "tryIt": "Try it now"
    },
    "showcase": {
      "eyebrow": "The product",
      "title": "Every screenshot below comes from the real app",
      "subtitle": "No mock-ups: screens from the production product, with demo data.",
      "cta": "Try it free",
      "autopilot": {
        "eyebrow": "Autopilot",
        "title": "From URL to plan in minutes",
        "body": "Paste your product address. Karexia understands it, writes the brief, then prepares strategy, content, communities and measurement.",
        "b1": "Reads at most 5 pages of your site, robots.txt honoured",
        "b2": "You confirm the site is yours before analysis",
        "b3": "Every external action stays subject to your approval",
        "alt": "Karexia Autopilot screenshot: a product brief generated from a URL and an action plan."
      },
      "today": {
        "eyebrow": "Daily Moves",
        "title": "What to do today, without overthinking",
        "body": "A short list of prioritised actions for your product, and a weekly review to adjust course.",
        "b1": "Priorities drawn from your brief and goals",
        "b2": "Weekly review to measure and correct",
        "b3": "Built for founders with an hour a day",
        "alt": "Karexia Today page screenshot with the day’s priority actions."
      },
      "composer": {
        "eyebrow": "Content and publishing",
        "title": "One composer, all your networks",
        "body": "Write once, adapt per network, schedule in an editorial calendar and publish, with AI at every step.",
        "b1": "Per-network options: LinkedIn polls, Facebook format (post, Reel, story)",
        "b2": "Bulk media publishing with a format type per network",
        "b3": "Queue with recycling of evergreen content",
        "alt": "Karexia multi-network composer screenshot with a draft post."
      },
      "aiTeam": {
        "eyebrow": "AI team",
        "title": "Agents do the work, you make the calls",
        "body": "Assemble a team of specialised agents and set each one’s autonomy: from approval before any outbound action to automatic publishing.",
        "b1": "Autonomy level adjustable per profile",
        "b2": "Prior approval for prospecting by default",
        "b3": "Optional 24/7 continuous mode, CASL compliance always applied",
        "alt": "Karexia AI team screenshot with autonomy levels and approvals."
      },
      "crm": {
        "eyebrow": "CRM",
        "title": "A CRM that enriches and scores itself",
        "body": "Contacts, companies and pipeline in one place, with lead scoring, organisation records and Apollo enrichment.",
        "b1": "Contact score and organisation data (firmographics)",
        "b2": "Apollo enrichment: uses AI credits, shown before you run it",
        "b3": "Contacts, companies, groups and deals linked to your content",
        "alt": "Karexia CRM screenshot: contact list with score and enrichment panel."
      },
      "email": {
        "eyebrow": "Purpose emails",
        "title": "The right email for every situation",
        "body": "A library of purpose-based templates in French, English and Arabic, ready to become drafts linked to your contacts.",
        "b1": "8 built-in purposes: fundraising, investor update, financial support, technical support, content creation, ambassadors, press, partnership",
        "b2": "Preview with sample data: nothing is sent, no credit used",
        "b3": "Duplicate a built-in template to customise it",
        "alt": "Karexia purpose email template library screenshot."
      },
      "outreach": {
        "eyebrow": "Compliant prospecting",
        "title": "Prospect without risking your reputation",
        "body": "Geo-targeted B2B search, email sequences with LinkedIn, SMS, WhatsApp or call steps, and a consent check before every send.",
        "b1": "CASL / Law 25 consent check before sending",
        "b2": "Non-email steps handled as tasks to confirm",
        "b3": "B2B search sources including Apollo, when you opt in",
        "alt": "Karexia prospecting screenshot with the CASL and Law 25 consent check."
      },
      "analytics": {
        "eyebrow": "Measurement",
        "title": "Know what works, and why",
        "body": "Analytics, attribution and reports, plus visibility in AI engines (GEO), site audit and Search Console.",
        "b1": "Performance dashboards and reports",
        "b2": "Attribution of results to published content",
        "b3": "Site audit and GEO visibility to get cited by AI engines",
        "alt": "Karexia analytics screenshot: performance and attribution."
      }
    },
    "reels": {
      "eyebrow": "Reels and short video",
      "title": "From script to Reel without leaving Karexia",
      "subtitle": "An idea becomes a script, captioned clips, an assembled Reel, then a post in the right format on each network.",
      "f1Title": "Video scripts by format",
      "f1Text": "Generate a script for a Reel, a Short, a TikTok or a YouTube video.",
      "f2Title": "Video and audio studio",
      "f2Text": "Transcription with SRT subtitles, captioned clips, voiceover, cloned voice, talking avatar and Reel assembly.",
      "f3Title": "One idea, many formats",
      "f3Text": "Automatic repurposing produces a post pack, a carousel, an infographic, an animated GIF and a Reel.",
      "f4Title": "The right format per network",
      "f4Text": "Bulk publishing with one type per network (post, Reel, story, carousel, video) and a Facebook post, Reel or story selector in the composer.",
      "note": "Availability depends on your plan (content studio) and your network connections. AI actions that use credits show their cost before they run.",
      "cta": "Create my first Reel",
      "imgAlt": "Karexia video studio screenshot with the Reel mode and automatic repurposing."
    },
    "how": {
      "eyebrow": "How it works",
      "title": "From zero to a running presence in four steps",
      "s1Title": "Paste your URL",
      "s1Text": "Autopilot reads your site, with your consent, and writes the product brief.",
      "s2Title": "Approve the plan",
      "s2Text": "You correct the brief and choose channels and the autonomy level.",
      "s3Title": "Create and schedule",
      "s3Text": "Content, Reels and posts land in the calendar, ready for approval.",
      "s4Title": "Measure and adjust",
      "s4Text": "Daily Moves, the weekly review and analytics close the loop."
    },
    "founder": {
      "eyebrow": "Founder mode",
      "title": "A lighter workspace for the solo founder",
      "text": "Founder mode keeps only the essentials in the menu to launch and grow your product: strategy, content, publishing, analytics and Copilot. Everything else stays reachable under “More”.",
      "b1": "Founder loop: product brief, daily moves, weekly review",
      "b2": "Switch to Team, Agency or Enterprise mode as you grow",
      "b3": "Hidden modules remain reachable by direct link",
      "cta": "Start in Founder mode"
    },
    "compare": {
      "eyebrow": "Why Karexia",
      "title": "One platform instead of a stack of tools",
      "colTopic": "Need",
      "colBefore": "With separate tools",
      "colAfter": "With Karexia",
      "r1Topic": "Create",
      "r1Before": "One tool for text, another for visuals, another for video",
      "r1After": "One studio for text, visuals, carousels, video and Reels",
      "r2Topic": "Publish",
      "r2Before": "One scheduler per network",
      "r2After": "One composer and calendar for all your networks",
      "r3Topic": "Prospect",
      "r3Before": "CSV exports between the search tool, the CRM and email",
      "r3After": "CRM, enrichment, templates and sequences in one place",
      "r4Topic": "Stay compliant",
      "r4Before": "Manual consent checks",
      "r4After": "A CASL / Law 25 check before every send",
      "r5Topic": "Delegate",
      "r5Before": "You do everything, all the time",
      "r5After": "A team of AI agents with approvals set to your comfort",
      "r6Topic": "Measure",
      "r6Before": "Spreadsheets and screenshots",
      "r6After": "Analytics, attribution, reports and a weekly review"
    },
    "proof": {
      "languagesValue": "3",
      "languagesLabel": "languages: French, English, Arabic",
      "purposesValue": "8",
      "purposesLabel": "built-in email purposes",
      "approvalValue": "L2",
      "approvalLabel": "approval before any outbound action, default for prospecting"
    },
    "trust": {
      "eyebrow": "Trust and compliance",
      "title": "Built so you stay in control",
      "subtitle": "Real safeguards, present in the product.",
      "i1Title": "CASL / Law 25 check",
      "i1Text": "Every prospecting send goes through a consent check that blocks when in doubt.",
      "i2Title": "Human approvals",
      "i2Text": "You set each agent’s autonomy. For prospecting, prior approval is the default setting.",
      "i3Title": "Sites read with your consent",
      "i3Text": "Autopilot only analyses sites you attest are yours, up to 5 pages, robots.txt honoured.",
      "i4Title": "Visible AI costs",
      "i4Text": "Actions that use AI credits show their cost before they run.",
      "i5Title": "Proof with written consent",
      "i5Text": "Published case studies carry the customer’s written consent and are removed as soon as they ask.",
      "linkPrivacy": "Privacy",
      "linkTerms": "Terms of use",
      "linkDeletion": "Data deletion"
    },
    "faq": {
      "eyebrow": "Frequently asked questions",
      "title": "Your questions, answered",
      "q1": "What is Karexia?",
      "a1": "An AI-powered marketing co-founder: Autopilot from a URL, content and video studio, multi-network composer and calendar, a team of AI agents, CRM and prospecting, analytics.",
      "q2": "Is there a free plan?",
      "a2": "Yes. The Pricing section shows the live plan catalogue, including the free plan with monthly AI credits. Included features depend on the plan.",
      "q3": "Does Karexia publish without my approval?",
      "a3": "No, unless you decide so. You set each agent’s autonomy, from prior approval to automatic publishing, and Autopilot submits every external action for your approval.",
      "q4": "Which networks can I publish to?",
      "a4": "The list is read live from Karexia’s publisher registry and shown in the “Why Karexia” section: LinkedIn, Facebook, Instagram, X, YouTube, TikTok and more.",
      "q5": "Is prospecting compliant?",
      "a5": "Every send goes through a consent check designed for CASL and Law 25. It helps you meet your obligations, without replacing legal advice.",
      "q6": "What are AI credits?",
      "a6": "AI actions, such as generating content or enriching a contact, use credits. Every plan includes some each month, and the cost is shown before you run an action.",
      "q7": "Which languages does Karexia support?",
      "a7": "The interface and the email templates are available in French, English and Arabic."
    },
    "cta": {
      "title": "Ready to launch your marketing co-founder?",
      "text": "Create an account, paste your product URL and approve your first plan.",
      "primary": "Create a free account",
      "secondary": "Sign in"
    },
    "media": {
      "play": "Play",
      "pause": "Pause"
    },
    "seo": {
      "title": "Karexia — AI marketing co-founder: Autopilot, content, Reels, CRM",
      "description": "Paste your URL: Karexia builds the marketing plan, creates content and Reels, publishes to your networks, and runs CRM and CASL / Law 25 compliant prospecting.",
      "ogAlt": "Karexia, the AI-powered marketing co-founder"
    }
  },
};

export default en;
